// ============================================================
// THE COMPANY — ConversationLog Component
// ============================================================

import { useEffect, useRef } from "react";
import { useCompanyStore } from "../../store/useCompanyStore";
import ConversationEntry from "./ConversationEntry";

interface ConversationLogProps {
  roleId: string;
}

export default function ConversationLog({ roleId }: ConversationLogProps) {
  const conversations = useCompanyStore((s) => s.conversations[roleId]);
  const fetchConversations = useCompanyStore((s) => s.fetchConversations);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Fetch conversations if not yet loaded for this role
  useEffect(() => {
    if (!conversations) {
      fetchConversations(roleId);
    }
  }, [roleId, conversations, fetchConversations]);

  // Scroll to bottom only on new additions, not deletions
  const prevLengthRef = useRef(0);
  useEffect(() => {
    const len = conversations?.length ?? 0;
    if (len > prevLengthRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
    prevLengthRef.current = len;
  }, [conversations]);

  if (!conversations) {
    return (
      <section>
        <h3
          className="text-xs font-semibold uppercase tracking-wider mb-3"
          style={{ color: "var(--text-muted)" }}
        >
          Conversation Log
        </h3>
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>
          Loading conversations...
        </p>
      </section>
    );
  }

  return (
    <section>
      <h3
        className="text-xs font-semibold uppercase tracking-wider mb-3"
        style={{ color: "var(--text-muted)" }}
      >
        Conversation Log
      </h3>

      {conversations.length === 0 ? (
        <div className="empty-state">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
          </svg>
          <p>No conversations yet</p>
        </div>
      ) : (
        <div className="space-y-5 max-h-[400px] overflow-y-auto pr-1">
          {conversations.map((convo) => (
            <ConversationEntry key={convo.id} conversation={convo} />
          ))}
          <div ref={bottomRef} />
        </div>
      )}
    </section>
  );
}
